import {
  AssigneeWarning,
  AssignmentCancelTypeEnum,
  AssignmentDeviationEnum,
  AssignmentModel,
  AssignmentRequestStatusEnum,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  CaseExtendedModel,
  CaseModel,
  CaseStatusEnum,
  CaseTypeEnum,
  ExpectedExpenseDto,
  AssignmentModel as LegacyAssignmentModel,
  CaseModel as LegacyCaseModel,
  PredefinedAction,
} from 'api/model';
import AuthorizedTextButton from 'components/inputs/AuthorizedTextButton';
import KeyValueList from 'components/KeyValueList';
import useTranslations from 'contexts/basicData/useTranslations';
import {
  formatDateTime,
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';

import NewExpenseModal from '../Expenses/NewExpenseModal';
import FileDropZone from 'components/Files/FileDropZone';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { arraySpreadIf } from 'utils/spreading';
import { getElectricCarBufferLabel } from 'utils/electricCarBuffer';
import {
  caseTypeDefaultsToSendsCustomerUpdateMessages,
  caseTypeShowsCustomerUpdateMessagesToggle,
  isFacilityToFacilityLikeCaseType,
  replacesCopyWithCopyOverMultipleDays,
} from './utils';
import FormattedAddress from 'components/FormattedAddress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCancel,
  faClock,
  faClone,
  faExternalLink,
  faPause,
  faTrashAlt,
  faTruckMoving,
  faUserAlt,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import StandardizedComments from 'components/StandardizedComments/StandardizedComments';
import { generatePath, useNavigate } from 'react-router-dom';
import useModalStack from 'contexts/modal/useModalStack';
import { useCopyCaseState } from 'contexts/basicData/useCopyCase';
import { getDefaultAssignment } from '../DetailedCaseFormProvider/useCaseAssignmentForms';
import { getRouteForCaseType } from 'hooks/useCurrentRouteCaseType';
import CreateCaseModal from 'components/CreateCaseModal';
import { useDetailedCaseContext } from '../DetailedCaseProvider';
import {
  useAssignmentGetAssignedAssignmentsForCarTransportTruckAssignment,
  useAssignmentGetCarTransportTruckAssignmentsForAssignment,
  useAssignmentMarkAssignmentInNeedOfHandlingAsHandled,
} from 'api/assignment/assignment';
import TextButton from 'components/inputs/TextButton';
import AutoApproveCheckDropdown from 'components/AssignmentApproval/AutoApproveCheckDropdown';
import InvoiceExtraDropdown from 'components/AssignmentApproval/InvoiceExtraDropdown';
import {
  getManualApprovalUnlockTime,
  isManualApprovalUnlocked,
} from 'components/AssignmentApproval/constants';
import useMe from 'contexts/authentication/useMe';
import Roles, {
  DefaultWebAdminPolicy,
  includesRoles,
  isAuthorized,
} from 'constants/Roles';
import Routes from 'constants/Routes';
import { useCaseGetAssignmentOverlaps } from 'api/case/case';
import useAssignmentWarnings from 'hooks/useAssignmentWarnings';
import useHasAccessToInvoiceExtras from 'hooks/useHasAccessToInvoiceExtras';
import styled, { css, useTheme } from 'styled-components';
import TooltipInfoWarning from 'components/TooltipInfoWarning';
import useAssignmentWarningsAndMultiplePurchases from 'hooks/useAssignmentWarningsAndMultiplePurchases';
import { useUserGetDrivingPolicyReportForUnPlannedAssignment } from 'api/user/user';
import WeeklyHoursStayOverWarning, {
  isStayOverTypeSelected,
} from '../WeeklyHoursStayOverWarning';
import { userNamesExcemptFromEstimationValidation } from 'constants/AppConstants';
import CaseAssignmentAvailabilityWidget from './CaseAssignmentAvailabilityWidget';
import WorkHoursSpan from './WorkHoursSpan';
import ReturnAssignmentFinder from './ReturnAssignmentFinder';
import TooltipInfo from 'components/TooltipInfo';
import useUsers from 'contexts/basicData/useUsers';
import ReplacePlaceholderCarModal from './ReplacePlaceholderCarModal';
import PredefinedActionMenu from 'components/PredefinedActions/PredefinedActionMenu';
import CopyOverMultipleDaysModal from 'components/PredefinedActions/CopyOverMultipleDaysModal';
import { LabelList, LabelTextButton } from './style';
import AssignCarsToCarTransportTruckModal from './AssignCarsToCarTransportTruckModal';
import ViewSegmentsModal from './ViewSegmentsModal';
import EstimatedAssignmentTimeValue from './EstimatedAssignmentTimeValue';
import useEstimatedWorkTime from '../DetailedCaseFormProvider/useEstimatedWorkTime';
import LabelWrap from 'components/inputs/LabelWrap';
import RichMessage from 'components/RichMessage';
import LoadedCarButton from './LoadedCarButton';
import { isSelfTransportationAssignment } from '../DetailedCaseFormProvider/types';
import FieldTesterBadge from 'components/FieldTesterBadge';
import UserModalLink from 'components/UserModalLink';
import { RideAlongCarrierSummary } from './RideAlongCarrierPicker';

const Wrapper = styled.div<{ assignmentStatus: AssignmentStatusEnum }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 10px 0;

  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  opacity: ${({ assignmentStatus }) =>
    assignmentStatus === AssignmentStatusEnum.Discarded ? 0.5 : 1};
`;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const StyledKVList = styled(KeyValueList)`
  b {
    font-weight: 600;
  }
`;

const Actions = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-end;
  min-width: 100px;
  padding-left: 10px;
  position: relative;
`;

const MyLabelWrap = styled(LabelWrap)`
  background-color: ${({ theme }) => theme.colors.background.secondary};
  padding: 5px;
`;

// const EllipsisWrapper = styled.div`
//   display: block;
//   color: ${({ theme }) => theme.colors.background.button};
//   padding: 5px;
// `;

const BadgeSpan = styled.span<{ statusId: AssignmentStatusEnum }>`
  padding: 1px;
  border-radius: 3px;
  background-color: ${({ theme, statusId }) =>
    statusId === AssignmentStatusEnum.Approved
      ? theme.colors.background.positive
      : theme.colors.background.primary};
  color: ${({ theme, statusId }) =>
    statusId === AssignmentStatusEnum.Approved
      ? theme.colors.foreground.button
      : theme.colors.foreground.primary};
`;

const Warning = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.background.negative};
  `}
`;

const Highlight = styled.div`
  ${({ theme }) => css`
    color: ${theme.colors.foreground.tint};
  `}
`;

const nonCancelableAssignmentStatuses: Set<AssignmentStatusEnum> = new Set([
  AssignmentStatusEnum.Canceled,
  AssignmentStatusEnum.Aborted,
  AssignmentStatusEnum.Approved,
  AssignmentStatusEnum.Discarded,
  AssignmentStatusEnum.CanceledNotFinished,
  AssignmentStatusEnum.AbortedNotFinished,
]);

// const nonStopOverableAssignmentStatuses: Set<AssignmentStatusEnum> = new Set([
//   AssignmentStatusEnum.Created,
//   AssignmentStatusEnum.Planned,
//   AssignmentStatusEnum.Assigned,
//   AssignmentStatusEnum.Accepted,
//   AssignmentStatusEnum.Complete,
//   AssignmentStatusEnum.Approved,
//   AssignmentStatusEnum.Canceled,
//   AssignmentStatusEnum.Aborted,
//   AssignmentStatusEnum.Discarded,
// ]);

const approvableAssignmentStatuses: Set<AssignmentStatusEnum> = new Set([
  AssignmentStatusEnum.Complete,
  AssignmentStatusEnum.Aborted,
  AssignmentStatusEnum.Canceled,
]);

interface CaseAssignmentProps {
  assignment: AssignmentModel;
  assignmentCase: CaseExtendedModel;
  currentCaseType: CaseTypeEnum;
  editMode: boolean;
  onEditClick(assignment: AssignmentModel): void;
  onCancelClick(assignmentId: number): void;
  onStopOverClick(assignmentId: number): void;
  onPlannedStopOverClick(
    assignmentId: number,
    preselectedCompanyId?: number
  ): void;
  onDiscardClick(assignmendId: number): void;
  onApproveClick(assignmentId: number): void;
  onOnHoldClick(assignmentId: number): void;
  showMarkedAsHandled?: boolean;
}

const CaseAssignment: React.FC<CaseAssignmentProps> = ({
  assignment,
  assignmentCase,
  currentCaseType,
  editMode,
  onEditClick,
  onCancelClick,
  onStopOverClick,
  onPlannedStopOverClick,
  onDiscardClick,
  onApproveClick,
  onOnHoldClick,
  showMarkedAsHandled,
}) => {
  const theme = useTheme();
  const assignmentWarnings = useAssignmentWarnings();
  const users = useUsers();
  const loggedInUser = useMe();
  const hasAccessToInvoiceExtras = useHasAccessToInvoiceExtras();
  const navigate = useNavigate();
  const modalStack = useModalStack();
  const getOverlapsCall = useCaseGetAssignmentOverlaps();
  const { caseResponse } = useDetailedCaseContext();

  const [overlaps, setOverlaps] = useState<AssignmentViewModel[]>([]);
  const [showSegmentsModal, setShowSegmentsModal] = useState(false);

  useEffect(() => {
    const getCaseOverlaps = async () => {
      const updatedAssignment = assignment as AssignmentModel;
      const result = await getOverlapsCall.mutateAsync({
        data: updatedAssignment,
      });

      setOverlaps(result ?? []);
    };

    getCaseOverlaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assignment.assignmentID,
    assignment.startTime,
    assignment.endTime,
    assignment.estimatedStartTime,
    assignment.estimatedDuration,
  ]);

  const [, setCaseCopy] = useCopyCaseState();
  const [showNewExpenseModal, setShowNewExpenseModal] = useState(false);
  const [showCopyOverDays, setShowCopyOverDays] = useState(false);
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  // const [showPredefinedActions, setShowPredefinedActions] = useState(false);
  const [predefinedActionType, setPredefinedActionType] =
    useState<PredefinedAction>();
  const {
    assignmentStatuses,
    assignmentTypes,
    assignmentDeviations,
    expenseTypes,
    // predefinedActions,
    cancelTypes,
    assignmentRequestStatuses,
    loadingStatuses,
    stayOverTypes,
  } = useTranslations();

  const {
    assignmentID,
    assignmentDeviationID,
    assignmentCancelTypeID,
    assignmentTypeID,
    sendCustomerUpdateMessages,
    autoApprove,
    assignmentStatusID,

    fromName,
    fromAddress,
    fromCity,
    fromZip,
    fromCounty,
    fromLatitude,
    fromLongitude,

    toName,
    toAddress,
    toCity,
    toZip,
    toCounty,
    toLatitude,
    toLongitude,

    bookedTo,
    startTime,
    endTime,
    breakTime,
    assignmentTimeDisplay,
    fixedAssignmentTime,
    workedTimeDisplay,
    assignedTo,
    extraInfo,

    driverSwapGroupID,
    ridesAlongWithAssignmentID,

    // followUpAssignment,

    estimatedDuration,
    modifiedEstimatedDuration,
    modifiedEstimatedDurationByID,
    estimatedStartTime,

    expectedExpenses,
    invoiceExtras,
    assignedToRating,
    assignedToRatingReason,

    drivingPolicyAssignmentInNeedOfHandling,

    standByAssignmentTime,
    isStandbyEligible,
    standbyAssignmentTimeReason,
  } = assignment;

  // Skiftet: benen som delar grupp ligger på DETTA ärende (A1 och A2), så de finns redan i
  // assignmentList — ingen extra uppslagning behövs. Ordningen ger vilket ben som är vilket, och
  // därmed var mötet sker: första benets slut, senare bens start.
  const swapLegs = useMemo(
    () =>
      driverSwapGroupID
        ? assignmentCase.case.assignmentList
            .filter((a) => a.driverSwapGroupID === driverSwapGroupID)
            .sort(
              (a, b) =>
                (a.estimatedStartTime ?? a.bookedTo).valueOf() -
                (b.estimatedStartTime ?? b.bookedTo).valueOf()
            )
        : [],
    [assignmentCase.case.assignmentList, driverSwapGroupID]
  );
  const swapLegIndex = swapLegs.findIndex((a) => a.assignmentID === assignmentID);
  const swapMeetingPlace =
    swapLegIndex === 0 ? toName || toCity : fromName || fromCity;
  const swapPartnerDriver = swapLegs.find(
    (a) => a.assignmentID !== assignmentID
  )?.assignedTo?.name;

  const { estimatedWorkTimeMillis, estimatedWorkStartAndEndString } =
    useEstimatedWorkTime(assignment);

  // Laddningsbuffert-annotering för elbilar (+30 min inom län / +1 h mellan län).
  // Läsvyn visar det server-persisterade värdet (servern äger bufferten); klient-
  // beräkningen används bara som förhandsvisning för osparade formulär.
  const electricCarBufferLabel = getElectricCarBufferLabel(
    assignment.electricCarBufferTime ?? 0
  );

  const {
    multiplePurchasesList,
    sameTimePurchaseWithEstimatedDuration,
    isViolatingDrivingPolicy,
  } = useAssignmentWarningsAndMultiplePurchases(
    currentCaseType,
    assignment,
    true
  );

  const missingExpectedExpenses = useMemo((): ExpectedExpenseDto[] => {
    // Check if all expected expense types are present
    const existingExpensesTypes = assignmentCase.expenses
      .filter((e) => e.assignmentID === assignmentID)
      .map((e) => e.expenseType);
    return expectedExpenses.filter(
      (e) => !existingExpensesTypes.includes(e.type)
    );
  }, [assignmentCase.expenses, assignmentID, expectedExpenses]);

  const markAsHandled = useAssignmentMarkAssignmentInNeedOfHandlingAsHandled();

  const onMarkAsHandled = async () => {
    if (!markAsHandled.isLoading) {
      await markAsHandled.mutateAsync({
        params: { assignmentId: assignment.assignmentID },
      });
    }
  };

  const handleCopyCaseAssignment = useCallback(() => {
    const caseCopy: LegacyCaseModel = {
      ...structuredClone(assignmentCase.case),
    };

    if (caseCopy) {
      // replace id with -1 to indicate that it is a new case
      caseCopy.caseID = -1;
      // set case status to open
      caseCopy.caseStatusID = CaseStatusEnum.Open;
      caseCopy.closedDate = undefined;
      caseCopy.completedDate = undefined;
      // remove ride order id
      caseCopy.rideOrderID = undefined;
      // remove invoice data id
      caseCopy.invoiceDataID = undefined;
      // Nolla TL:ens direktbok-svar — kopian är ett nytt ärende och TL behöver
      // svara på nytt (eventuellt annorlunda än originalet).
      caseCopy.isDirectBooking = undefined;

      // remove original address info
      caseCopy.originalFromName = undefined;
      caseCopy.originalFromAddress = undefined;
      caseCopy.originalFromCity = undefined;
      caseCopy.originalFromZip = undefined;
      caseCopy.originalFromCounty = undefined;
      caseCopy.originalToName = undefined;
      caseCopy.originalToAddress = undefined;
      caseCopy.originalToCity = undefined;
      caseCopy.originalToZip = undefined;
      caseCopy.originalToCounty = undefined;

      caseCopy.assignmentList = caseCopy.assignmentList
        .filter((a) => a.assignmentID === assignmentID) // only the current assignment
        .map(
          (a): LegacyAssignmentModel => ({
            ...getDefaultAssignment(),
            // replace ids with -1 to indicate that it is a new assignment
            assignmentID: -1,
            caseID: -1,
            // replace assignment status & deviation with default values
            assignmentStatusID: AssignmentStatusEnum.Created,
            assignmentDeviationID: AssignmentDeviationEnum.None,
            // values to copy (autoApprove medvetet inte kopierad — kopian är ett nytt
            // uppdrag och ska få defaulten true, inte ärva källans ev. avgodkännande-reset)
            assignmentTypeID: a.assignmentTypeID,
            bookedTo: a.bookedTo,
            breakTime: 0,
            estimatedDuration: a.estimatedDuration,
            systemEstimatedDurationApprox: a.systemEstimatedDurationApprox,
            systemEstimatedDurationExact: a.systemEstimatedDurationExact,
            // extraInfo: a.extraInfo, <-- This should not be passed on as it is not relevant for the new assignment
            expectedExpenses: a.expectedExpenses,
            fromName: a.fromName,
            fromAddress: a.fromAddress,
            fromCity: a.fromCity,
            fromZip: a.fromZip,
            fromCounty: a.fromCounty,
            toName: a.toName,
            sendCustomerUpdateMessages:
              caseTypeDefaultsToSendsCustomerUpdateMessages(currentCaseType),
            toAddress: a.toAddress,
            toCity: a.toCity,
            toZip: a.toZip,
            toCounty: a.toCounty,
            fixedAssignmentTime: a.fixedAssignmentTime,
            allowPrivateStopOver: a.allowPrivateStopOver,
            // Planeringsavsikt knuten till rutten — följer med kopian precis som mellanlandningen.
            shouldGoOnTruck: a.shouldGoOnTruck,
          })
        );

      setCaseCopy(caseCopy);

      const caseTypeRoute = getRouteForCaseType(caseCopy.caseTypeID);
      navigate(generatePath(caseTypeRoute, { id: -1 }));
      modalStack.push(
        <CreateCaseModal
          caseType={caseCopy.caseTypeID}
          onCaseCreated={(copyCaseId: number) => {
            const newpath = `${caseTypeRoute}/${copyCaseId}`;
            navigate(newpath);
            modalStack.pop();
          }}
          onClose={() => modalStack.pop()}
        />
      );
    } else {
      setCaseCopy(null);
    }
  }, [
    assignmentCase.case,
    assignmentID,
    currentCaseType,
    modalStack,
    navigate,
    setCaseCopy,
  ]);

  useEffect(() => {
    if (showNewExpenseModal) {
      setPredefinedActionType(undefined);
    }
  }, [showNewExpenseModal]);

  useEffect(() => {
    if (predefinedActionType) {
      setShowNewExpenseModal(false);
    }
  }, [predefinedActionType]);

  const isAssignmentApprovable = (
    _assignmentStatusID: AssignmentStatusEnum,
    _hasEndTime: boolean
  ) => {
    if (
      // Approvable statuses are always approvable
      approvableAssignmentStatuses.has(_assignmentStatusID) &&
      _hasEndTime
    ) {
      return true;
    }
    return false;
  };

  // Manuellt godkännande är spärrat i backend till kl 02 dagen efter startdagen (samma gräns
  // som det nattliga autogodkännandet — har snurran haft sin chans får admin godkänna direkt).
  // Gäller bara övergången Slutförd → Godkänd (avbokade/bomkörda berörs inte). Avgodkända
  // uppdrag (autoApprove nollställd vid avgodkännandet) är undantagna — de har redan godkänts
  // en gång och får om-godkännas direkt. Externa lastbilstransportörer (isTruckTransport) är
  // också undantagna och får godkännas direkt efter slutförande.
  const manualApprovalUnlockAt = getManualApprovalUnlockTime(
    startTime,
    endTime
  );
  const manualApprovalLocked =
    assignmentStatusID === AssignmentStatusEnum.Complete &&
    autoApprove !== false &&
    !isManualApprovalUnlocked(startTime, endTime, assignedTo?.isTruckTransport);

  // Externa lastbilstransportörer omfattas inte av körtidspolicyn, så
  // körtidspolicy-varningen (varningstriangeln med "Bryter mot körtidspolicyn!")
  // ska inte visas för dem. Tillgänglighetsvarningen är ett separat regelverk och
  // visas fortfarande. Jfr körtidsvyerna i tillgänglighetstabellen/-modalen.
  const isExternalTruckTransport = !!assignedTo?.isTruckTransport;

  const derivedDuration = useMemo(() => {
    if (fixedAssignmentTime) {
      return fixedAssignmentTime;
    }
    if (startTime && endTime) {
      return new Date(endTime).getTime() - new Date(startTime).getTime();
    }

    // Ej påbörjat uppdrag: laddbufferten är belagd tid och ska in i det fönster
    // körtidspolicykollen prövar (faktisk/fast tid ovan påverkas inte av den).
    return (estimatedDuration ?? 0) + (assignment.electricCarBufferTime ?? 0);
  }, [
    fixedAssignmentTime,
    startTime,
    endTime,
    estimatedDuration,
    assignment.electricCarBufferTime,
  ]);

  const drivingPolicyReportResult =
    useUserGetDrivingPolicyReportForUnPlannedAssignment(
      {
        userId: assignment.assignedTo?.userID,
        bookedTo: assignment.bookedTo,
        startOrEstimatedStartTime:
          assignment.startTime ?? assignment.estimatedStartTime,
        endOrEstimatedEndTime: assignment.endTime,
        estimatedDuration: derivedDuration,
        excludeAssignmentId: assignment.assignmentID,
      },
      {
        query: {
          enabled:
            !!assignment.assignedTo &&
            !userNamesExcemptFromEstimationValidation.includes(
              assignment.assignedTo.name
            ),
        },
      }
    );

  const transportTruckAssignments =
    useAssignmentGetAssignedAssignmentsForCarTransportTruckAssignment(
      {
        assignmentId: assignment.assignmentID,
      },
      {
        query: {
          enabled:
            assignment.assignmentID !== -1 &&
            assignmentCase.case.caseTypeID === CaseTypeEnum.CarTransportTruck,
        },
      }
    );

  const loadedByTransport =
    useAssignmentGetCarTransportTruckAssignmentsForAssignment(
      {
        assignmentId: assignment.assignmentID,
      },
      {
        query: {
          enabled: assignment.assignmentID !== -1,
        },
      }
    );

  const getDurationString = (am: AssignmentModel) => {
    const start = am.startTime ?? am.estimatedStartTime;
    let end = am.endTime;
    if (!end && am.estimatedDuration && start) {
      end = new Date(start.getTime() + am.estimatedDuration);
    }
    if (!end) {
      return '';
    }
    return `(${formatTime(start)} - ${formatTime(end)})`;
  };

  const warnings = useMemo(() => {
    return assignmentWarnings.getAssignmentWarnings(assignment);
  }, [assignment, assignmentWarnings]);

  const rows = [
    ...arraySpreadIf(
      !!assignment.publicToken &&
        // current user is not efterkontroll or ekonomi
        (!includesRoles(loggedInUser?.roles ?? [], [
          Roles.Efterkontroll,
          Roles.Ekonomi,
        ]) ||
          includesRoles(loggedInUser?.roles ?? [], [Roles.SuperAdmin])),

      {
        key: 'Publik länk (för kund)',
        value: (
          <Highlight>
            <a
              style={{ color: theme.colors.foreground.tint }}
              href={`https://app.zakraleveranser.se/track/?t=${assignment.publicToken}&mode=1`}
              // href={`http://localhost:3000/track/?t=${assignment.publicToken}`}
              target="_blank"
              rel="noreferrer"
              title="Öppna den publika länken i en ny flik, länken är giltig till dess att uppdraget är på väg tillbaka."
            >
              Följ leveransen <FontAwesomeIcon icon={faExternalLink} />
              {/* http://localhost:3000/track/?t={assignment.publicToken} */}
            </a>
          </Highlight>
        ),
      }
    ),
    ...arraySpreadIf(
      !!assignment.publicToken &&
        // current user is not efterkontroll or ekonomi
        (!includesRoles(loggedInUser?.roles ?? [], [
          Roles.Efterkontroll,
          Roles.Ekonomi,
        ]) ||
          includesRoles(loggedInUser?.roles ?? [], [Roles.SuperAdmin])),

      {
        key: 'Publik länk (för bokare/internt)',
        value: (
          <Highlight>
            <a
              style={{ color: theme.colors.foreground.tint }}
              href={`https://app.zakraleveranser.se/track/?t=${assignment.publicToken}&mode=1`}
              // href={`http://localhost:3000/track/?t=${assignment.publicToken}&mode=1`}
              target="_blank"
              rel="noreferrer"
              title="Öppna den publika länken i en ny flik, länken är giltig till dess att uppdraget är på väg tillbaka."
            >
              Följ leveransen <FontAwesomeIcon icon={faExternalLink} />
              {/* http://localhost:3000/track/?t={assignment.publicToken}&mode=1 */}
            </a>
          </Highlight>
        ),
      }
    ),
    ...arraySpreadIf(!!driverSwapGroupID && swapLegs.length > 1, {
      key: 'Skifte',
      value: (
        <Highlight>
          Ben {swapLegIndex + 1} av {swapLegs.length}. Fordonet byter förare vid{' '}
          {swapMeetingPlace || 'mötespunkten'}
          {swapPartnerDriver ? ` — ${swapPartnerDriver} kör det andra benet` : ''}.
        </Highlight>
      ),
    }),
    ...arraySpreadIf(warnings.length > 0, {
      key: 'Varningar',
      value: (
        <div>
          {warnings.map((w) => (
            <Warning key={w.warning}>
              {w.icon ?? <FontAwesomeIcon icon={faWarning} />} {w.warning}
            </Warning>
          ))}
        </div>
      ),
    }),
    ...arraySpreadIf(overlaps.length > 0, {
      key: 'Överlappande uppdrag',
      value: (
        <div>
          {overlaps.map((o) => (
            <TextButton
              key={o.assignment.assignmentID}
              onClick={() => navigate(`/sok/${o.case.caseID}`)}
              title={`Överlappar med uppdrag ${o.case.registrationNumber} - ${o.assignment.assignmentID}`}
              style={{ color: 'red' }}
            >
              {`${o.case.registrationNumber} ${o.assignment.assignmentID} ${getDurationString(o.assignment as AssignmentModel)}`}{' '}
              <FontAwesomeIcon icon={faExternalLink} />
            </TextButton>
          ))}
        </div>
      ),
    }),
    ...arraySpreadIf((loadedByTransport.data?.length ?? 0) > 0, {
      key: 'Tilldelad till lastbil',
      value: (
        <LabelList>
          {loadedByTransport.data?.map((t) => (
            <LabelTextButton
              key={t.carTransportTruckAssignmentID}
              onClick={() =>
                window.open(
                  `${Routes.internalDeliveryGroup.carTransportTrucks.index}/${t.carTransportTruckCaseID}`,
                  t.carTransportTruckAssignmentID.toString() ?? '_blank'
                )
              }
              title={`Tilldelad till lastbil ${t.carTransportTruckRegistrationNumber}`}
            >
              <span>{t.carTransportTruckRegistrationNumber}</span>
              <FontAwesomeIcon icon={faTruckMoving} />
              <span>{loadingStatuses[t.loadingStatus]}</span>
            </LabelTextButton>
          ))}
        </LabelList>
      ),
    }),
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
      key: 'Tilldelade bilar',
      value: (
        <LabelList>
          {transportTruckAssignments.data?.map((t) => (
            <LoadedCarButton
              key={t.loadedAssignmentID}
              mainAssignment={assignment}
              loadedAssignment={t}
            />
          ))}
          <AssignCarsToCarTransportTruckModal
            assignment={assignment}
            onUpdate={() => {
              caseResponse.update();
            }}
          />
        </LabelList>
      ),
    }),
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
      key: 'Segment',
      value: (
        <TextButton
          style={{ paddingLeft: 0 }}
          onClick={() => setShowSegmentsModal(true)}
        >
          Visa och hantera segment
        </TextButton>
      ),
    }),
    ...arraySpreadIf(assignment.assignmentRequests.length > 0, {
      key: 'Annonsförfrågan',
      value: (
        <>
          <TextButton
            style={{
              paddingLeft: 0,
              color: (
                [
                  AssignmentRequestStatusEnum.Denied,
                  AssignmentRequestStatusEnum.Removed,
                ] as AssignmentRequestStatusEnum[]
              ).includes(
                assignment.assignmentRequests[0]?.assignmentRequestStatusID
              )
                ? 'red'
                : undefined,
              textDecoration: (
                [
                  AssignmentRequestStatusEnum.Denied,
                  AssignmentRequestStatusEnum.Removed,
                ] as AssignmentRequestStatusEnum[]
              ).includes(
                assignment.assignmentRequests[0]?.assignmentRequestStatusID
              )
                ? 'line-through'
                : undefined,
            }}
            onClick={() =>
              window.open(
                `${Routes.internalDeliveryGroup.requests.index}/${assignment.assignmentRequests[0]?.requestID}`,
                `annonsforfragan${assignment.assignmentRequests[0]?.requestID}`
              )
            }
            title="Annonsförfrågan"
          >
            {assignment.assignmentRequests[0]?.targetStartTime
              ? `${formatDateTime(
                  assignment.assignmentRequests[0]?.targetStartTime
                )}`
              : ''}{' '}
            <FontAwesomeIcon icon={faExternalLink} />
          </TextButton>
          <span>
            (
            {
              assignmentRequestStatuses[
                assignment.assignmentRequests[0]?.assignmentRequestStatusID
              ]
            }
            )
          </span>
        </>
      ),
    }),
    { key: 'Uppdrags-ID', value: assignmentID === -1 ? '-' : assignmentID },
    ...arraySpreadIf(
      assignment.assignmentStatusID === AssignmentStatusEnum.Approved &&
        assignment.assignmentDeviationID === AssignmentDeviationEnum.None &&
        includesRoles(loggedInUser?.roles ?? [], [
          Roles.GoSupport,
          Roles.SuperAdmin,
        ]),
      {
        key: 'Fälttestarutvärdering',
        value: assignedToRating ?? '-',
      }
    ),
    ...arraySpreadIf(
      assignment.assignmentStatusID === AssignmentStatusEnum.Approved &&
        assignment.assignmentDeviationID === AssignmentDeviationEnum.None &&
        includesRoles(loggedInUser?.roles ?? [], [
          Roles.GoSupport,
          Roles.SuperAdmin,
        ]),
      {
        key: 'Fälttestarutvärdering anledning',
        value: assignedToRatingReason ?? '-',
      }
    ),
    ...arraySpreadIf(multiplePurchasesList.length > 0, {
      key: 'Flerbilsinköp',
      value: (
        <>
          {multiplePurchasesList.map((ass) => (
            <TextButton
              key={ass.assignment.assignmentID}
              onClick={() =>
                window.open(
                  `/sok/${ass.case.caseID}`,
                  ass.case.caseID.toString() ?? '_blank'
                )
              }
              title={`Flerbilsinköp med ${ass.case.registrationNumber}`}
            >
              {ass.case.registrationNumber}{' '}
              {!!ass.assignment.estimatedDuration &&
                ass.assignment.assignedTo?.userID ===
                  assignment.assignedTo?.userID && (
                  <FontAwesomeIcon icon={faClock} />
                )}
              {ass.assignment.assignedTo?.userID ===
                assignment.assignedTo?.userID && (
                <FontAwesomeIcon
                  title={ass.assignment.assignedTo?.name}
                  icon={faUserAlt}
                />
              )}{' '}
              <FontAwesomeIcon icon={faExternalLink} />
            </TextButton>
          ))}
        </>
      ),
    }),
    {
      key: 'Bokad till',
      value: (
        <span>
          <span>{bookedTo ? formatDateTime(bookedTo) : '-'}</span>
        </span>
      ),
    },
    { key: 'Typ', value: assignmentTypes[assignmentTypeID] },
    {
      key: 'Status',
      value: (
        <BadgeSpan statusId={assignmentStatusID}>
          {assignmentStatuses[assignmentStatusID]}
        </BadgeSpan>
      ),
    },
    ...arraySpreadIf(
      assignmentDeviationID !== AssignmentDeviationEnum.None &&
        assignmentStatusID === AssignmentStatusEnum.Approved,
      {
        key: 'Avvikelse',
        value: assignmentDeviations[assignmentDeviationID],
      }
    ),
    {
      key: 'Tillåt privat mellanlandning',
      value: assignment.allowPrivateStopOver ? 'Ja' : 'Nej',
    },
    // "Ska på lastbil" ligger per ben; visas inte på lastbilsuppdraget självt.
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.CarTransportTruck, {
      key: 'Ska på lastbil',
      value: assignment.shouldGoOnTruck ? 'Ja' : 'Nej',
    }),
    // "Nyckel inlämnad" gäller bara leveranser som förbereds på anläggning
    // (hemleverans/butiksleverans/flytt mellan anläggningar). Nullable — ej angivet visas som '-'.
    ...arraySpreadIf(
      currentCaseType === CaseTypeEnum.HomeDelivery ||
        isFacilityToFacilityLikeCaseType(currentCaseType),
      {
        key: 'Nyckel inlämnad',
        value:
          assignment.keyHandedIn === undefined || assignment.keyHandedIn === null
            ? '-'
            : assignment.keyHandedIn
              ? 'Ja'
              : 'Nej',
      }
    ),
    ...arraySpreadIf(
      assignmentCancelTypeID !== null && !Number.isNaN(assignmentCancelTypeID),
      {
        key: 'Avbokningstyp',
        value: cancelTypes[assignmentCancelTypeID as AssignmentCancelTypeEnum],
      }
    ),
    ...arraySpreadIf(
      caseTypeShowsCustomerUpdateMessagesToggle(currentCaseType),
      {
        key: 'Skicka SMS',
        value: sendCustomerUpdateMessages ? <b>Ja</b> : 'Nej',
      }
    ),
    {
      // Read-only: flaggan kan inte längre redigeras i UI:t. null betyder "inte
      // opt-out:ad" (legacy-rader) och ska visas som Ja — bara explicit false
      // (avgodkännande-reset) visas som Nej.
      key: 'Auto-godkänn',
      value: autoApprove !== false ? <b>Ja</b> : 'Nej',
    },

    // Uthyrd personal (RentedStaff): från == till (en arbetsplats). Visa bara
    // en adressrad i ärendevyn; backend speglar to <- from så båda fälten finns
    // kvar i BE/db med samma värde.
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.RentedStaff, {
      key: 'Arbetsplats',
      value: (
        <FormattedAddress
          address={fromAddress}
          city={fromCity}
          county={fromCounty?.areaName}
          name={fromName}
          zipCode={fromZip}
          latitude={fromLatitude}
          longitude={fromLongitude}
        />
      ),
    }),
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.RentedStaff, {
      key: 'Från',
      value: (
        <FormattedAddress
          address={fromAddress}
          city={fromCity}
          county={fromCounty?.areaName}
          name={fromName}
          zipCode={fromZip}
          latitude={fromLatitude}
          longitude={fromLongitude}
        />
      ),
    }),
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.RentedStaff, {
      key: 'Till',
      value: (
        <FormattedAddress
          address={toAddress}
          city={toCity}
          county={toCounty?.areaName}
          name={toName}
          zipCode={toZip}
          latitude={toLatitude}
          longitude={toLongitude}
        />
      ),
    }),
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.Samakning, {
      key: 'Samåker med',
      value: ridesAlongWithAssignmentID ? (
        <RideAlongCarrierSummary
          bookedTo={bookedTo}
          carrierAssignmentId={ridesAlongWithAssignmentID}
        />
      ) : (
        '-'
      ),
    }),
    {
      key: 'Fälttestare',
      value: assignedTo ? (
        <span>
          <FieldTesterBadge
            user={assignedTo}
            roles={loggedInUser?.roles ?? []}
          />
          {isAuthorized(
            DefaultWebAdminPolicy.allow,
            DefaultWebAdminPolicy.deny,
            loggedInUser?.roles ?? []
          ) && <UserModalLink userId={assignedTo.userID} />}{' '}
          {assignedTo &&
            loggedInUser &&
              isAuthorized(DefaultWebAdminPolicy.allow, DefaultWebAdminPolicy.deny, loggedInUser.roles) && (
              <CaseAssignmentAvailabilityWidget
                user={assignedTo}
                date={estimatedStartTime ?? bookedTo}
              />
            )}
          {(assignment.assigneeIsUnavailableOnDate ||
            (!isExternalTruckTransport &&
              ((drivingPolicyReportResult.data &&
                isViolatingDrivingPolicy(drivingPolicyReportResult.data)) ||
                drivingPolicyReportResult.data?.warnings.includes(
                  AssigneeWarning.CannotStayAtHotels
                )))) && (
            <TooltipInfoWarning
              isUnavailableViolation={assignment.assigneeIsUnavailableOnDate}
              warnings={
                isExternalTruckTransport
                  ? undefined
                  : drivingPolicyReportResult.data?.warnings
              }
              user={assignedTo}
              showOnlyTooltip
            />
          )}
          {currentCaseType !== CaseTypeEnum.Placeholder &&
            assignedTo.userID &&
            assignmentID >= 0 && (
              <div style={{ gap: 5, display: 'inline-flex' }}>
                <WorkHoursSpan
                  drivinPolicyReport={drivingPolicyReportResult.data}
                />
                {loggedInUser &&
                  includesRoles(
                    DefaultWebAdminPolicy.allow,
                    loggedInUser.roles
                  ) && (
                    <ReturnAssignmentFinder
                      assignmentModel={assignment}
                      caseModel={assignmentCase.case}
                      drivingPolicyReport={drivingPolicyReportResult.data}
                    />
                  )}
              </div>
            )}
        </span>
      ) : undefined,
    },
    {
      key:
        currentCaseType === CaseTypeEnum.CarTransportTruck
          ? 'Estimerad körtid'
          : 'Estimerad uppdragstid',
      value: (
        <EstimatedAssignmentTimeValue
          assignment={assignment}
          caseType={currentCaseType}
          sameTimePurchaseWithEstimatedDuration={
            sameTimePurchaseWithEstimatedDuration
          }
        />
      ),
    },
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
      key: 'Beräknad tid för',
      value: (
        <Horizontal
          style={{
            gap: 10,
            justifyContent: 'space-between',
            maxWidth: 100,
          }}
        >
          <MyLabelWrap
            label={<span style={{ fontSize: '0.9em' }}>lastning</span>}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 5,
              }}
            >
              {assignment.estimatedLoadingTime
                ? getHoursAndMinutesFromMillisecondsString(
                    assignment.estimatedLoadingTime
                  )
                : '-'}
            </div>
          </MyLabelWrap>
          <MyLabelWrap
            label={<span style={{ fontSize: '0.9em' }}>lossning</span>}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 5,
              }}
            >
              {assignment.estimatedUnloadingTime
                ? getHoursAndMinutesFromMillisecondsString(
                    assignment.estimatedUnloadingTime
                  )
                : '-'}
            </div>
          </MyLabelWrap>
        </Horizontal>
      ),
    }),
    ...arraySpreadIf(currentCaseType === CaseTypeEnum.CarTransportTruck, {
      key: 'Total arbetstid',
      value: (
        <span>
          {getHoursAndMinutesFromMillisecondsString(estimatedWorkTimeMillis)} (
          {estimatedWorkStartAndEndString})
        </span>
      ),
    }),
    // sameTimePurchaseWithEstimatedDuration={sameTimePurchaseWithEstimatedDuration}
    // sameTimePurchaseStartAndEndTimeString={sameTimePurchaseStartAndEndTimeString}
    // startAndEndTimeString={startAndEndTimeString}

    // fixedAssignmentTime ? (
    //   <span>
    //     {getHoursAndMinutesFromMillisecondsString(fixedAssignmentTime)}
    //   </span>
    // ) : estimatedDuration ? (
    //   <span>
    //     {getHoursAndMinutesFromMillisecondsString(estimatedDuration)}
    //     {` (${startAndEndTimeString})`}
    //   </span>
    // ) : sameTimePurchaseWithEstimatedDuration?.assignment
    //     .estimatedDuration ? (
    //   <span style={{ opacity: 0.5 }}>
    //     {getHoursAndMinutesFromMillisecondsString(
    //       sameTimePurchaseWithEstimatedDuration.assignment.estimatedDuration
    //     )}
    //     {` (${sameTimePurchaseStartAndEndTimeString}, samma som ${sameTimePurchaseWithEstimatedDuration.case.registrationNumber})`}
    //   </span>
    // ) : undefined
    // />
    // },
    ...arraySpreadIf(isSelfTransportationAssignment(assignmentTypeID), {
      key: 'Fälttestaren bokar egen resa',
      value: assignment.assigneeShouldBookTheirOwnTicket ? 'Ja' : 'Nej',
    }),
    ...arraySpreadIf(isSelfTransportationAssignment(assignmentTypeID), {
      key: 'Biljett-information',
      value: <RichMessage text={assignment.ticketInfo ?? ''} />,
    }),
    ...arraySpreadIf(
      currentCaseType !== CaseTypeEnum.Placeholder &&
        (assignmentCancelTypeID ===
          AssignmentCancelTypeEnum.CanceledNotStartedSameDay ||
          assignmentCancelTypeID ===
            AssignmentCancelTypeEnum.CanceledNotArrived ||
          assignmentCancelTypeID === AssignmentCancelTypeEnum.CanceledArrived),
      {
        key: 'Ska ha standby-tid',
        value: (
          <span>
            {isStandbyEligible === true
              ? 'Ja'
              : isStandbyEligible === false
                ? 'Nej'
                : ''}
          </span>
        ),
      }
    ),
    ...arraySpreadIf(
      currentCaseType !== CaseTypeEnum.Placeholder &&
        (assignmentCancelTypeID ===
          AssignmentCancelTypeEnum.CanceledNotStartedSameDay ||
          assignmentCancelTypeID ===
            AssignmentCancelTypeEnum.CanceledNotArrived ||
          assignmentCancelTypeID === AssignmentCancelTypeEnum.CanceledArrived),
      {
        key: 'Standby-tid',
        value: (
          <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {standByAssignmentTime === 0
              ? '0h'
              : standByAssignmentTime
                ? getHoursAndMinutesFromMillisecondsString(
                    standByAssignmentTime
                  )
                : '--:--'}
            <TooltipInfo
              minWidth$={200}
              info="Standby-tid används för löneunderlaget. Tiden beräknas automatiskt dagen efter avbokning för: Icke-överlappande uppdragstid och upp till max standby-tid."
            />
          </span>
        ),
      }
    ),
    ...arraySpreadIf(
      currentCaseType !== CaseTypeEnum.Placeholder &&
        !!standbyAssignmentTimeReason,
      {
        key: 'Standby-tid anledning',
        value: standbyAssignmentTimeReason,
      }
    ),
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Estimerad starttid',
      value: estimatedStartTime ? formatDateTime(estimatedStartTime) : '-',
    }),

    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Starttid',
      value: startTime ? formatDateTime(startTime) : '-',
    }),
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Sluttid',
      value: endTime ? formatDateTime(endTime) : '-',
    }),

    {
      key: 'Övernattning',
      value: (
        <Horizontal style={{ alignItems: 'center', gap: 8 }}>
          {assignment.stayOverTypeID !== null
            ? stayOverTypes[assignment.stayOverTypeID!]
            : 'NEJ'}
          <WeeklyHoursStayOverWarning
            stayOverTypeIdSelected={isStayOverTypeSelected(
              assignment.stayOverTypeID
            )}
            report={drivingPolicyReportResult.data}
          />
        </Horizontal>
      ),
    },
    ...arraySpreadIf(
      // stayOverTypeID can be 0 (HOTEL), so check against null/undefined
      // rather than truthiness; null is what the API sends for no stayover
      assignment.stayOverTypeID !== undefined &&
        assignment.stayOverTypeID !== null,
      {
        // show stay over info
        key: 'Övernattningsinfo',
        value: <RichMessage text={assignment.stayOverInfo ?? ''} />,
      }
    ),

    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Uppdragstid',
      value: assignmentTimeDisplay,
    }),
    {
      key: 'Matrast',
      value: `${breakTime}min`,
    },

    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Arbetad tid',
      value: workedTimeDisplay,
      disabled: fixedAssignmentTime !== undefined && fixedAssignmentTime > 0,
    }),
    ...arraySpreadIf(currentCaseType !== CaseTypeEnum.Placeholder, {
      key: 'Estimerad tid',
      value: estimatedDuration ? (
        <span>
          {getHoursAndMinutesFromMillisecondsString(estimatedDuration)}
          {electricCarBufferLabel && (
            <span
              style={{ marginLeft: 6, opacity: 0.75 }}
              title="Automatiskt tillägg på estimerad tid för att ta höjd för laddning av elbil"
            >
              {electricCarBufferLabel}
            </span>
          )}
          {modifiedEstimatedDuration && modifiedEstimatedDurationByID > 0 && (
            <span>
              {` (lagd av ${users[modifiedEstimatedDurationByID]?.name ?? '-'}) `}
              <TooltipInfo
                info={`Den senaste satta estimerade tiden innan uppdraget påbörjades gjordes av ${users[modifiedEstimatedDurationByID]?.name ?? '-'} vid ${formatDateTime(modifiedEstimatedDuration)}`}
                minWidth$={200}
              />
            </span>
          )}
        </span>
      ) : undefined,
    }),
    {
      key: 'Fast tid',
      value:
        fixedAssignmentTime && fixedAssignmentTime > 0
          ? getHoursAndMinutesFromMillisecondsString(fixedAssignmentTime)
          : '-',
    },
  ];

  rows.push({
    key: 'Förväntade utgifter',
    value: (
      <div style={{ gap: 5, display: 'inline-flex' }}>
        {expectedExpenses.reduce((acc, cur) => {
          if (acc === '') {
            return `${expenseTypes[Number(cur.type)]}`;
          }
          return `${acc}, ${expenseTypes[Number(cur.type)]}`;
        }, '')}
        {missingExpectedExpenses.length > 0 && (
          <Highlight>
            Ännu ej uppladdade:{' '}
            {missingExpectedExpenses.map((m) => (
              <span key={m.type}>{expenseTypes[Number(m.type)]}</span>
            ))}
          </Highlight>
        )}
      </div>
    ),
  });

  const canEditInvoiceExtras = includesRoles(loggedInUser?.roles ?? [], [
    Roles.SuperAdmin,
    Roles.Efterkontroll,
  ]);
  if (hasAccessToInvoiceExtras) {
    rows.push({
      key: 'Fakturera extra',
      value: (
        <InvoiceExtraDropdown
          assignmentId={assignmentID}
          invoiceExtras={invoiceExtras ?? []}
          readOnly={!canEditInvoiceExtras}
          onDataChanged={() => caseResponse.update()}
        />
      ),
    });
  }

  // Samma roller som efterkontrollvyn (endpointen är också låst till dem).
  const canSeeAutoApproveChecks = canEditInvoiceExtras;
  if (canSeeAutoApproveChecks && currentCaseType !== CaseTypeEnum.Placeholder) {
    rows.push({
      key: 'Autogodkännande',
      value: (
        <AutoApproveCheckDropdown
          caseId={assignment.caseID}
          assignmentId={assignmentID}
          assignmentStatusID={assignmentStatusID}
          autoApprove={autoApprove}
          autoApprovedAt={assignment.autoApprovedAt}
        />
      ),
    });
  }

  if (currentCaseType !== CaseTypeEnum.Placeholder) {
    rows.push({
      key: 'Information från Ride',
      value: extraInfo,
    });

    // show the standardized comments contents (i.e. not just the title)
    rows.push({
      key: 'Information till fälttestaren',
      value: (
        <p style={{ whiteSpace: 'pre-wrap' }}>
          {assignment.standardizedComments.reduce((acc, cur, index) => {
            if (index === 0) {
              return `${cur.comment}`;
            }

            return `${acc}\n${cur.comment}`;
          }, '' as string)}
        </p>
      ),
    });
  }

  const handleFilesDropped = useCallback((files: File[]) => {
    // Only accept the first file — re-fetching after save unmounts queued
    // modals, so multi-file flow is unreliable. Users can drag remaining
    // files in one at a time once the first expense is saved.
    const [first] = files;
    if (first) setDroppedFile(first);
  }, []);

  const isExpenseModalOpen = showNewExpenseModal || droppedFile !== null;

  return (
    <FileDropZone
      onFilesDropped={handleFilesDropped}
      overlayLabel="Släpp för att skapa kvitto på detta uppdrag"
      disabled={isExpenseModalOpen}
    >
    <Wrapper assignmentStatus={assignmentStatusID}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <StyledKVList colonKey rows={rows} />
        <Suspense fallback="Loading...">
          {loggedInUser !== null &&
            isAuthorized(
              DefaultWebAdminPolicy.allow,
              DefaultWebAdminPolicy.deny,
              loggedInUser.roles
            ) && (
              <StandardizedComments
                assignmentId={assignmentID}
                commentsOnAssignment={assignment.standardizedComments}
                onRefreshCase={async () => {
                  await caseResponse?.update();
                }}
              />
            )}
        </Suspense>
      </div>

      {
        // external admins don't get access to the actions menu
        !loggedInUser?.roles.includes(Roles.ExternalAdmin) && (
          <>
            <Actions>
              <AuthorizedTextButton
                onClick={() => setShowNewExpenseModal(true)}
              >
                + Ny utgift
              </AuthorizedTextButton>
              {currentCaseType === CaseTypeEnum.Placeholder &&
                assignmentTypeID === AssignmentTypeEnum.CarPlaceholder && (
                  <ReplacePlaceholderCarModal
                    assignment={{ assignment, case: assignmentCase.case }}
                    drivingPolicyReport={drivingPolicyReportResult.data}
                    // onConfirm={() => {
                    //   modalStack.pop();
                    //   caseResponse?.update();
                    // }}
                  />
                )}
              {currentCaseType !== CaseTypeEnum.Placeholder && (
                <>
                  {replacesCopyWithCopyOverMultipleDays(currentCaseType) ? (
                    // Uthyrd personal, Samåkning & Däckkörning: ersätt vanliga kopieringen med
                    // "kopiera över flera dagar" (en kopia per dag i ett datumspann).
                    <AuthorizedTextButton
                      onClick={() => setShowCopyOverDays(true)}
                    >
                      <FontAwesomeIcon icon={faClone} /> Kopiera över flera dagar
                    </AuthorizedTextButton>
                  ) : (
                    <>
                      <AuthorizedTextButton onClick={handleCopyCaseAssignment}>
                        <FontAwesomeIcon icon={faClone} /> Kopiera
                      </AuthorizedTextButton>
                      {currentCaseType === CaseTypeEnum.CarTransportTruck && (
                        // Biltransport lastbil: vanliga kopieringen finns kvar, men
                        // erbjud även "kopiera över flera dagar" — här inkluderas
                        // helgdagar (till skillnad från Uthyrd personal/Samåkning).
                        <AuthorizedTextButton
                          onClick={() => setShowCopyOverDays(true)}
                        >
                          <FontAwesomeIcon icon={faClone} /> Kopiera över flera
                          dagar
                        </AuthorizedTextButton>
                      )}
                    </>
                  )}
                  {!nonCancelableAssignmentStatuses.has(assignmentStatusID) && (
                    <AuthorizedTextButton
                      onClick={() => onCancelClick(assignment.assignmentID)}
                    >
                      <FontAwesomeIcon icon={faCancel} /> Avboka
                    </AuthorizedTextButton>
                  )}
                  {showMarkedAsHandled && (
                    <AuthorizedTextButton onClick={() => onMarkAsHandled()}>
                      Markera som hanterad
                    </AuthorizedTextButton>
                  )}
                  {!nonCancelableAssignmentStatuses.has(assignmentStatusID) &&
                    assignmentStatusID < AssignmentStatusEnum.Started && (
                      <AuthorizedTextButton
                        onClick={() => onDiscardClick(assignment.assignmentID)}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} /> Ta bort
                      </AuthorizedTextButton>
                    )}
                  {assignmentStatusID >= AssignmentStatusEnum.Created &&
                    assignmentStatusID <= AssignmentStatusEnum.Accepted &&
                    assignmentStatusID !== AssignmentStatusEnum.OnHold && (
                    <AuthorizedTextButton
                      onClick={() => onOnHoldClick(assignment.assignmentID)}
                    >
                      <FontAwesomeIcon icon={faPause} /> Parkera
                    </AuthorizedTextButton>
                  )}
                  {isAssignmentApprovable(assignmentStatusID, !!endTime) && (
                    <AuthorizedTextButton
                      disabled={overlaps.length > 0 || manualApprovalLocked}
                      onClick={() => onApproveClick(assignment.assignmentID)}
                      title={
                        overlaps.length > 0
                          ? 'Överlappande uppdrag'
                          : manualApprovalLocked && manualApprovalUnlockAt
                            ? `Kan godkännas manuellt tidigast ${formatDateTime(manualApprovalUnlockAt)} — autogodkännandet ska hinna utvärdera uppdraget först`
                            : ''
                      }
                    >
                      Godkänn
                    </AuthorizedTextButton>
                  )}
                  {caseResponse.response && (
                    <PredefinedActionMenu
                      currentCaseType={currentCaseType}
                      caseModel={
                        caseResponse.response.case as unknown as CaseModel
                      }
                      assignment={assignment}
                      assignmentStatusID={assignmentStatusID}
                      editMode={editMode}
                      assignedTo={assignedTo}
                      drivingPolicyAssignmentInNeedOfHandling={
                        drivingPolicyAssignmentInNeedOfHandling
                      }
                      onEditClick={onEditClick}
                      onStopOverClick={onStopOverClick}
                      onPlannedStopOverClick={onPlannedStopOverClick}
                    />
                  )}
                </>
              )}
            </Actions>

            {showNewExpenseModal && (
              <NewExpenseModal
                assignmentId={assignment.assignmentID}
                onCancel={() => setShowNewExpenseModal(false)}
              />
            )}

            {droppedFile && (
              <NewExpenseModal
                assignmentId={assignment.assignmentID}
                initialFile={droppedFile}
                onCancel={() => setDroppedFile(null)}
              />
            )}

            {showSegmentsModal && (
              <ViewSegmentsModal
                assignmentId={assignment.assignmentID}
                onClose={() => setShowSegmentsModal(false)}
              />
            )}

            {showCopyOverDays && (
              <CopyOverMultipleDaysModal
                assignment={assignment}
                caseModel={assignmentCase.case as unknown as CaseModel}
                onClose={() => setShowCopyOverDays(false)}
              />
            )}
          </>
        )
      }
    </Wrapper>
    </FileDropZone>
  );
};

export default CaseAssignment;
